import type {CSSProperties} from "react";
import {token} from "@atlaskit/tokens";
import {clsx} from "~/components/table/tableUtils.tsx";
import {
    canRenderTableEmptyStatePortal,
    getTableEmptyStateOverlayStyle,
    renderTableEmptyStatePortal,
} from "~/components/table/tableEmptyStatePortal";
import {useTablePanelViewportRect} from "~/components/table/useTablePanelViewportRect";
import StatusWarningIcon from "@vesta/aergia/svgs/core/status-warning.svg?react";
import "~/components/table/tableNoSearchResultsEmptyState.css";

export function TableNoArchivedDataEmptyState({
    className,
    viewportBoundsElement,
    title = "No Archived data available",
    subtitle,
    illustrationNode,
}: {
    className?: string;
    viewportBoundsElement: HTMLElement | null;
    title?: string;
    subtitle?: string;
    illustrationNode?: React.ReactNode;
}) {
    const boundsRect = useTablePanelViewportRect(viewportBoundsElement);

    const overlayStyle: CSSProperties | undefined = getTableEmptyStateOverlayStyle({
        viewportBoundsElement: viewportBoundsElement,
        boundsRect: boundsRect,
    });

    const canPortal = canRenderTableEmptyStatePortal({
        viewportBoundsElement: viewportBoundsElement,
        boundsRect: boundsRect,
    });

    const overlay = (
        <div
            className={clsx("table-no-search-results", className)}
            style={overlayStyle}
            role="status"
            aria-live="polite"
        >
            <div className="table-no-search-results__inner">
                {illustrationNode !== undefined ? (
                    <div className="table-no-search-results__illustration mb-ds-space-200">{illustrationNode}</div>
                ) : (
                    <StatusWarningIcon
                        aria-hidden
                        className="table-no-archived-data__icon"
                        style={{color: token("color.icon.disabled")}}
                    />
                )}
                <h2 className="table-no-search-results__title" style={{color: token("color.text")}}>
                    {title}
                </h2>
                {subtitle !== undefined && (
                    <p className="table-no-search-results__subtitle" style={{color: token("color.text")}}>
                        {subtitle}
                    </p>
                )}
            </div>
        </div>
    );

    return (
        <>
            {canPortal
                ? renderTableEmptyStatePortal({
                      overlay: overlay,
                      viewportBoundsElement: viewportBoundsElement,
                      boundsRect: boundsRect,
                  })
                : null}
            <div className="table-no-search-results__table-anchor" aria-hidden />
        </>
    );
}
